import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

const PRODUCT_SCOPES = [
  { id: 'psa', label: 'PSA', description: 'Tickets, companies, time entries, contracts' },
  { id: 'crm', label: 'CRM', description: 'Contacts, deals, activities, pipeline' },
  { id: 'books', label: 'Books', description: 'Invoices, revenue, customers' },
  { id: 'rmm', label: 'RMM', description: 'Devices, alerts, patches' },
  { id: 'security', label: 'Security', description: 'Incidents, scans, vulnerabilities' },
  { id: 'cmdb', label: 'CMDB', description: 'Configurations, assets (never passwords)' },
  { id: 'oncall', label: 'On-Call', description: 'Schedules, escalations, rotations' },
  { id: 'projects', label: 'Projects', description: 'Tasks, milestones, project status' },
];

const MODEL_OPTIONS = [
  { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini', description: 'Fast, cost-effective' },
  { value: 'gpt-4.1', label: 'GPT-4.1', description: 'High accuracy, complex reasoning' },
  { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano', description: 'Fastest, simple tasks' },
  { value: 'gpt-4o', label: 'GPT-4o', description: 'Best overall performance' },
];

const EMOJI_OPTIONS = [
  '🤖','🎯','🛡️','💼','🔧','📊','🚀','⚡','🌐','🔍',
  '📋','💡','🤝','⏱️','📍','💰','📚','🎫','🔐','🏗️',
];

const TONE_OPTIONS = ['professional', 'friendly', 'technical', 'casual'] as const;
const VERBOSITY_OPTIONS = ['concise', 'balanced', 'detailed'] as const;
const PROACTIVITY_OPTIONS = ['reactive', 'moderate', 'proactive'] as const;

type Step = 1 | 2 | 3 | 4 | 5;

interface FormState {
  // Step 1: Identity
  name: string;
  description: string;
  avatar_emoji: string;
  color: string;
  personality: {
    tone: typeof TONE_OPTIONS[number];
    verbosity: typeof VERBOSITY_OPTIONS[number];
    proactivity: typeof PROACTIVITY_OPTIONS[number];
  };
  // Step 2: Instructions
  instructions: string;
  model: string;
  temperature: number;
  max_tokens: number;
  // Step 3: Knowledge
  product_scopes: string[];
  // Step 4: Tools
  enabled_tools: string[];
  require_confirmation: boolean;
  model_override_allowed: boolean;
  // Step 5: Sharing
  visibility: 'private' | 'team' | 'organization';
}

const DEFAULT_FORM: FormState = {
  name: '',
  description: '',
  avatar_emoji: '🤖',
  color: '#3B82F6',
  personality: { tone: 'professional', verbosity: 'balanced', proactivity: 'moderate' },
  instructions: '',
  model: 'gpt-4.1-mini',
  temperature: 0.7,
  max_tokens: 4096,
  product_scopes: [],
  enabled_tools: [],
  require_confirmation: false,
  model_override_allowed: true,
  visibility: 'private',
};

const STEP_LABELS: Record<Step, string> = {
  1: 'Identity',
  2: 'Instructions',
  3: 'Knowledge',
  4: 'Tools',
  5: 'Sharing',
};

function countTokens(text: string): number {
  // Rough estimate: ~4 chars per token
  return Math.ceil(text.length / 4);
}

export function AgentBuilder() {
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();
  const queryClient = useQueryClient();
  const isEdit = Boolean(agentId);

  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [urlInput, setUrlInput] = useState('');
  const [knowledgeSources, setKnowledgeSources] = useState<Array<{ type: 'url'; url: string; title?: string }>>([]);

  // Load existing agent if editing
  const { data: existingAgent, isLoading: agentLoading } = useQuery({
    queryKey: ['agent', agentId],
    queryFn: () => api<FormState & { id: string; knowledge_files?: { filename: string; blob_url: string }[] }>(`custom-gpts/${agentId}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existingAgent) {
      setForm({
        name: existingAgent.name,
        description: (existingAgent as unknown as { description?: string }).description ?? '',
        avatar_emoji: (existingAgent as unknown as { avatar_emoji?: string }).avatar_emoji ?? '🤖',
        color: (existingAgent as unknown as { color?: string }).color ?? '#3B82F6',
        personality: existingAgent.personality ?? DEFAULT_FORM.personality,
        instructions: (existingAgent as unknown as { instructions?: string }).instructions ?? '',
        model: existingAgent.model ?? 'gpt-4.1-mini',
        temperature: (existingAgent as unknown as { temperature?: number }).temperature ?? 0.7,
        max_tokens: (existingAgent as unknown as { max_tokens?: number }).max_tokens ?? 4096,
        product_scopes: existingAgent.product_scopes ?? [],
        enabled_tools: existingAgent.enabled_tools ?? [],
        require_confirmation: existingAgent.require_confirmation ?? false,
        model_override_allowed: existingAgent.model_override_allowed ?? true,
        visibility: existingAgent.visibility ?? 'private',
      });
    }
  }, [existingAgent]);

  const createMutation = useMutation({
    mutationFn: (body: Partial<FormState> & { short_description?: string }) =>
      api<{ id: string }>('custom-gpts', { body }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      navigate(`/agents/${data.id}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (body: Partial<FormState>) =>
      api(`custom-gpts/${agentId}`, { method: 'PATCH', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      navigate(`/agents/${agentId}`);
    },
  });

  const handleSave = () => {
    const payload = {
      name: form.name.trim(),
      short_description: form.description.trim(),
      description: form.description.trim(),
      avatar_emoji: form.avatar_emoji,
      color: form.color,
      personality: form.personality,
      instructions: form.instructions.trim(),
      model: form.model,
      temperature: form.temperature,
      max_tokens: form.max_tokens,
      product_scopes: form.product_scopes,
      enabled_tools: form.enabled_tools,
      require_confirmation: form.require_confirmation,
      model_override_allowed: form.model_override_allowed,
      visibility: form.visibility,
    };

    if (isEdit) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error ?? updateMutation.error;

  if (isEdit && agentLoading) {
    return <div className="p-8 text-center text-muted-foreground">Loading agent...</div>;
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate('/agents')} className="text-muted-foreground hover:text-foreground">
          ← Back
        </button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {isEdit ? 'Edit Agent' : 'Create Agent'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEdit ? 'Update your AI agent configuration' : 'Build a custom AI agent for your MSP workflows'}
          </p>
        </div>
      </div>

      {/* Step navigation */}
      <div className="flex items-center gap-1 mb-8">
        {([1, 2, 3, 4, 5] as Step[]).map((s, idx) => (
          <div key={s} className="flex items-center">
            <button
              onClick={() => setStep(s)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                step === s
                  ? 'bg-primary text-primary-foreground'
                  : step > s
                  ? 'text-primary hover:bg-primary/10'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                step > s ? 'bg-primary/20 text-primary' : 'border'
              }`}>
                {step > s ? '✓' : s}
              </span>
              {STEP_LABELS[s]}
            </button>
            {idx < 4 && <div className="w-6 h-px bg-border mx-1" />}
          </div>
        ))}
      </div>

      {/* Step 1: Identity */}
      {step === 1 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold mb-1">Agent Identity</h2>
            <p className="text-sm text-muted-foreground">Give your agent a name, personality, and visual identity.</p>
          </div>

          {/* Avatar + Name */}
          <div className="flex items-start gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">Avatar</label>
              <div className="flex flex-wrap gap-2 max-w-64">
                {EMOJI_OPTIONS.map(emoji => (
                  <button
                    key={emoji}
                    onClick={() => setForm(f => ({ ...f, avatar_emoji: emoji }))}
                    className={`w-9 h-9 text-lg rounded-lg flex items-center justify-center border transition-colors ${
                      form.avatar_emoji === emoji ? 'border-primary bg-primary/10' : 'hover:bg-muted border-transparent'
                    }`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Security Analyst, Triage Bot"
                  className="w-full border rounded-md px-3 py-2 bg-background text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="What does this agent do? Shown in the agent picker."
                  className="w-full border rounded-md px-3 py-2 bg-background text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Color</label>
                <div className="flex gap-2 flex-wrap">
                  {['#3B82F6','#10B981','#EF4444','#F59E0B','#8B5CF6','#EC4899','#06B6D4','#6366F1'].map(c => (
                    <button
                      key={c}
                      onClick={() => setForm(f => ({ ...f, color: c }))}
                      className={`w-7 h-7 rounded-full transition-all ${form.color === c ? 'ring-2 ring-offset-2 ring-primary scale-110' : ''}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="rounded-lg border bg-card p-4">
            <p className="text-xs text-muted-foreground mb-3 font-medium uppercase tracking-wide">Preview</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl" style={{ backgroundColor: `${form.color}20` }}>
                {form.avatar_emoji}
              </div>
              <div>
                <div className="font-medium text-foreground">{form.name || 'Agent Name'}</div>
                <div className="text-sm text-muted-foreground">{form.description || 'Agent description'}</div>
              </div>
            </div>
          </div>

          {/* Personality */}
          <div>
            <h3 className="text-sm font-semibold mb-3">Personality</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-muted-foreground mb-2">Tone</label>
                <div className="space-y-1">
                  {TONE_OPTIONS.map(t => (
                    <button
                      key={t}
                      onClick={() => setForm(f => ({ ...f, personality: { ...f.personality, tone: t } }))}
                      className={`w-full text-left px-3 py-1.5 rounded-md text-sm capitalize transition-colors ${
                        form.personality.tone === t ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-2">Verbosity</label>
                <div className="space-y-1">
                  {VERBOSITY_OPTIONS.map(v => (
                    <button
                      key={v}
                      onClick={() => setForm(f => ({ ...f, personality: { ...f.personality, verbosity: v } }))}
                      className={`w-full text-left px-3 py-1.5 rounded-md text-sm capitalize transition-colors ${
                        form.personality.verbosity === v ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-2">Proactivity</label>
                <div className="space-y-1">
                  {PROACTIVITY_OPTIONS.map(p => (
                    <button
                      key={p}
                      onClick={() => setForm(f => ({ ...f, personality: { ...f.personality, proactivity: p } }))}
                      className={`w-full text-left px-3 py-1.5 rounded-md text-sm capitalize transition-colors ${
                        form.personality.proactivity === p ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Instructions */}
      {step === 2 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold mb-1">Instructions</h2>
            <p className="text-sm text-muted-foreground">Write the system prompt that defines how your agent behaves.</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium">System Prompt <span className="text-red-500">*</span></label>
              <span className={`text-xs ${countTokens(form.instructions) > 4000 ? 'text-red-500' : 'text-muted-foreground'}`}>
                ~{countTokens(form.instructions).toLocaleString()} / 4,000 tokens
              </span>
            </div>
            <textarea
              value={form.instructions}
              onChange={(e) => setForm(f => ({ ...f, instructions: e.target.value }))}
              placeholder="You are a helpful MSP assistant that...&#10;&#10;Your role is to:&#10;1. ...&#10;2. ...&#10;&#10;Always be concise and technical."
              rows={16}
              className="w-full border rounded-md px-3 py-2 bg-background text-sm font-mono resize-none"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Tip: Be specific about the agent's role, constraints, and output format. Reference relevant context like client environment or business rules.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Model</label>
              <select
                value={form.model}
                onChange={(e) => setForm(f => ({ ...f, model: e.target.value }))}
                className="w-full border rounded-md px-3 py-2 bg-background text-sm"
              >
                {MODEL_OPTIONS.map(m => (
                  <option key={m.value} value={m.value}>{m.label} — {m.description}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Temperature: {form.temperature}</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={form.temperature}
                onChange={(e) => setForm(f => ({ ...f, temperature: parseFloat(e.target.value) }))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>Precise</span>
                <span>Creative</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="model-override"
              checked={form.model_override_allowed}
              onChange={(e) => setForm(f => ({ ...f, model_override_allowed: e.target.checked }))}
              className="w-4 h-4"
            />
            <label htmlFor="model-override" className="text-sm text-muted-foreground">
              Allow users to switch models when chatting with this agent
            </label>
          </div>
        </div>
      )}

      {/* Step 3: Knowledge */}
      {step === 3 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold mb-1">Knowledge & Data Access</h2>
            <p className="text-sm text-muted-foreground">Configure what data sources and products this agent can access.</p>
          </div>

          {/* Product Access */}
          <div>
            <h3 className="text-sm font-semibold mb-3">Product Data Access</h3>
            <p className="text-xs text-muted-foreground mb-3">Select which products this agent can read data from during conversations.</p>
            <div className="grid grid-cols-2 gap-3">
              {PRODUCT_SCOPES.map(scope => (
                <label
                  key={scope.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    form.product_scopes.includes(scope.id)
                      ? 'border-primary bg-primary/5'
                      : 'hover:bg-muted/50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={form.product_scopes.includes(scope.id)}
                    onChange={(e) => {
                      setForm(f => ({
                        ...f,
                        product_scopes: e.target.checked
                          ? [...f.product_scopes, scope.id]
                          : f.product_scopes.filter(s => s !== scope.id),
                      }));
                    }}
                    className="w-4 h-4 mt-0.5 shrink-0"
                  />
                  <div>
                    <div className="text-sm font-medium">{scope.label}</div>
                    <div className="text-xs text-muted-foreground">{scope.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* URL Knowledge Sources */}
          <div>
            <h3 className="text-sm font-semibold mb-3">URL Sources</h3>
            <p className="text-xs text-muted-foreground mb-3">Add URLs to crawl and include as reference knowledge. Content will be indexed for retrieval.</p>
            <div className="flex gap-2 mb-3">
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://docs.example.com/guide"
                className="flex-1 border rounded-md px-3 py-2 bg-background text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && urlInput.trim()) {
                    setKnowledgeSources(s => [...s, { type: 'url', url: urlInput.trim() }]);
                    setUrlInput('');
                  }
                }}
              />
              <button
                onClick={() => {
                  if (urlInput.trim()) {
                    setKnowledgeSources(s => [...s, { type: 'url', url: urlInput.trim() }]);
                    setUrlInput('');
                  }
                }}
                className="px-4 py-2 rounded-md text-sm border hover:bg-muted font-medium"
              >
                Add URL
              </button>
            </div>
            {knowledgeSources.length > 0 && (
              <div className="space-y-2">
                {knowledgeSources.map((src, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 rounded-md border bg-muted/30">
                    <span className="text-sm flex-1 truncate text-muted-foreground">{src.url}</span>
                    <button
                      onClick={() => setKnowledgeSources(s => s.filter((_, i) => i !== idx))}
                      className="text-xs text-red-500 hover:text-red-700 shrink-0"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Note: URL crawling happens after the agent is saved. You can also upload text files from the agent's detail page.
            </p>
          </div>
        </div>
      )}

      {/* Step 4: Tools */}
      {step === 4 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold mb-1">Tools & Capabilities</h2>
            <p className="text-sm text-muted-foreground">Configure what actions this agent can take in your ecosystem.</p>
          </div>

          {form.product_scopes.length === 0 ? (
            <div className="rounded-lg border bg-muted/30 p-8 text-center">
              <p className="text-sm text-muted-foreground">
                Select product data access in Step 3 to configure available tools.
              </p>
              <button onClick={() => setStep(3)} className="mt-3 text-sm text-primary hover:underline">
                ← Go to Knowledge
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {form.product_scopes.map(scopeId => {
                const scope = PRODUCT_SCOPES.find(s => s.id === scopeId);
                if (!scope) return null;
                const tools = [
                  `${scopeId}.read`,
                  `${scopeId}.create`,
                  `${scopeId}.update`,
                ];
                return (
                  <div key={scopeId} className="rounded-lg border p-4">
                    <h3 className="text-sm font-semibold mb-3">{scope.label} Tools</h3>
                    <div className="space-y-2">
                      {tools.map(tool => (
                        <label key={tool} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.enabled_tools.includes(tool)}
                            onChange={(e) => {
                              setForm(f => ({
                                ...f,
                                enabled_tools: e.target.checked
                                  ? [...f.enabled_tools, tool]
                                  : f.enabled_tools.filter(t => t !== tool),
                              }));
                            }}
                            className="w-4 h-4"
                          />
                          <div>
                            <span className="text-sm font-mono">{tool}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              {tool.endsWith('.read') ? 'Read data' : tool.endsWith('.create') ? 'Create records' : 'Update records'}
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex items-start gap-3 p-4 rounded-lg border bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800">
            <span className="text-amber-500 shrink-0">⚠️</span>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.require_confirmation}
                  onChange={(e) => setForm(f => ({ ...f, require_confirmation: e.target.checked }))}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  Require confirmation before write actions
                </span>
              </label>
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                When enabled, the agent will ask for user confirmation before creating or updating any records.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Step 5: Sharing */}
      {step === 5 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold mb-1">Sharing & Visibility</h2>
            <p className="text-sm text-muted-foreground">Control who can find and use this agent.</p>
          </div>

          <div className="space-y-3">
            {[
              { value: 'private', label: 'Private', description: 'Only you can see and use this agent', icon: '🔒' },
              { value: 'team', label: 'Team', description: 'Visible to members of your team', icon: '👥' },
              { value: 'organization', label: 'Organization', description: 'Everyone in your organization can use this agent', icon: '🏢' },
            ].map(opt => (
              <label
                key={opt.value}
                className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                  form.visibility === opt.value ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                }`}
              >
                <input
                  type="radio"
                  name="visibility"
                  value={opt.value}
                  checked={form.visibility === opt.value}
                  onChange={(e) => setForm(f => ({ ...f, visibility: e.target.value as typeof form.visibility }))}
                  className="w-4 h-4 mt-0.5 shrink-0"
                />
                <div>
                  <div className="text-sm font-medium">{opt.icon} {opt.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{opt.description}</div>
                </div>
              </label>
            ))}
          </div>

          <div className="rounded-lg border bg-card p-4">
            <h3 className="text-sm font-semibold mb-2">Summary</h3>
            <div className="space-y-1 text-sm text-muted-foreground">
              <div className="flex gap-2">
                <span className="font-medium text-foreground">Name:</span>
                {form.name || '(not set)'}
              </div>
              <div className="flex gap-2">
                <span className="font-medium text-foreground">Model:</span>
                {form.model}
              </div>
              <div className="flex gap-2">
                <span className="font-medium text-foreground">Product access:</span>
                {form.product_scopes.length > 0 ? form.product_scopes.join(', ') : 'None'}
              </div>
              <div className="flex gap-2">
                <span className="font-medium text-foreground">Tools:</span>
                {form.enabled_tools.length} enabled
              </div>
              <div className="flex gap-2">
                <span className="font-medium text-foreground">Visibility:</span>
                {form.visibility}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t">
        <button
          onClick={() => step > 1 ? setStep((step - 1) as Step) : navigate('/agents')}
          className="px-4 py-2 rounded-md text-sm border hover:bg-muted font-medium"
        >
          {step === 1 ? 'Cancel' : '← Back'}
        </button>
        <div className="flex items-center gap-3">
          {error && (
            <p className="text-sm text-red-500">
              {error instanceof Error ? error.message : 'Save failed'}
            </p>
          )}
          {step < 5 ? (
            <button
              onClick={() => setStep((step + 1) as Step)}
              disabled={step === 1 && !form.name.trim()}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
            >
              Next: {STEP_LABELS[(step + 1) as Step]} →
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={!form.name.trim() || !form.instructions.trim() || isPending}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
            >
              {isPending ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Agent')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
